import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Grid,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Switch,
} from "@mui/material";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

function WhatsAppTemplateBuilderPage() {
  const pageTitle = "WhatsApp - Create Template";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");

  const [title, setTitle] = useState("");
  const [templateCode, setTemplateCode] = useState("");
  const [category, setCategory] = useState("MARKETING");
  const [language, setLanguage] = useState("en_US");

  const [headerEnabled, setHeaderEnabled] = useState(false);
  const [headerFormat, setHeaderFormat] = useState("TEXT");
  const [headerText, setHeaderText] = useState("");

  const [body, setBody] = useState("");

  const [footerEnabled, setFooterEnabled] = useState(false);
  const [footer, setFooter] = useState("");

  const [buttons, setButtons] = useState([]);

  const [variableExamples, setVariableExamples] = useState({});

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  /*
   * -----------------------------------------
   * VARIABLES
   * -----------------------------------------
   */

  const extractVariables = (text) => {
    if (!text) {
      return [];
    }

    const regex = /\{\{([a-zA-Z0-9_]+)\}\}/g;
    const variables = [];
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (!variables.includes(match[1])) {
        variables.push(match[1]);
      }
    }

    return variables;
  };

  const formatVariableLabel = (code) => {
    return code.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getVariables = (text) => {
    return extractVariables(text).map((code, index) => ({
      code: code,
      label: formatVariableLabel(code),
      position: index + 1,
      example: variableExamples[code] || "",
    }));
  };

  const updateVariableExample = (code, value) => {
    setVariableExamples((current) => ({
      ...current,
      [code]: value,
    }));
  };

  const renderPreviewText = (text) => {
    if (!text) {
      return "Your message will appear here.";
    }

    return text.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (match, code) => {
      return variableExamples[code] || formatVariableLabel(code);
    });
  };

  const renderVariableEditor = (variables) => {
    if (!variables || variables.length === 0) {
      return null;
    }

    return (
      <MDBox mt={2}>
        <MDTypography variant="button" fontWeight="medium" display="block" mb={0.5}>
          Personalization
        </MDTypography>

        <MDTypography variant="caption" color="text.secondary" display="block" mb={1.5}>
          Provide an example value for each variable. These examples are submitted to Meta for
          template review.
        </MDTypography>

        {variables.map((code) => (
          <MDBox
            key={code}
            mb={1.5}
            p={1.5}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.75}>
              {formatVariableLabel(code)}
            </MDTypography>

            <MDTypography variant="caption" color="text.secondary" display="block" mb={1}>
              {"{{" + code + "}}"}
            </MDTypography>

            <MDInput
              fullWidth
              size="small"
              placeholder={`Example for {{${code}}}`}
              value={variableExamples[code] || ""}
              onChange={(e) => updateVariableExample(code, e.target.value)}
            />
          </MDBox>
        ))}
      </MDBox>
    );
  };

  /*
   * -----------------------------------------
   * BUTTONS
   * -----------------------------------------
   */

  const addButton = () => {
    if (buttons.length >= 3) {
      setSeverity("error");
      setErrMsg("Maximum number of buttons reached.");
      setOpen(true);
      return;
    }

    setButtons([
      ...buttons,
      {
        type: "QUICK_REPLY",
        text: "",
      },
    ]);
  };

  const updateButton = (index, key, value) => {
    const updated = [...buttons];

    updated[index] = {
      ...updated[index],
      [key]: value,
    };

    setButtons(updated);
  };

  const removeButton = (index) => {
    setButtons(buttons.filter((_, buttonIndex) => buttonIndex !== index));
  };

  /*
   * -----------------------------------------
   * COMPONENT BUILDER
   * -----------------------------------------
   */

  const buildComponents = () => {
    const components = [];

    if (headerEnabled) {
      const header = {
        type: "HEADER",
        format: headerFormat,
      };

      if (headerFormat === "TEXT") {
        header.text = headerText;

        const variables = getVariables(headerText);

        if (variables.length > 0) {
          header.variables = variables;
        }
      }

      components.push(header);
    }

    if (body) {
      const bodyComponent = {
        type: "BODY",
        text: body,
      };

      const variables = getVariables(body);

      if (variables.length > 0) {
        bodyComponent.variables = variables;
      }

      components.push(bodyComponent);
    }

    if (footerEnabled && footer) {
      components.push({
        type: "FOOTER",
        text: footer,
      });
    }

    if (buttons.length > 0) {
      components.push({
        type: "BUTTONS",
        buttons: buttons.map((button) => {
          const updatedButton = {
            ...button,
          };

          if (button.type === "URL" && button.url) {
            const variables = getVariables(button.url);

            if (variables.length > 0) {
              updatedButton.variables = variables;
            }
          }

          return updatedButton;
        }),
      });
    }

    return components;
  };

  /*
   * -----------------------------------------
   * SAVE
   * -----------------------------------------
   */

  const saveTemplate = async () => {
    if (!title.trim()) {
      setSeverity("error");
      setErrMsg("Enter a template name.");
      setOpen(true);
      return;
    }

    if (!templateCode.trim()) {
      setSeverity("error");
      setErrMsg("Enter a template code.");
      setOpen(true);
      return;
    }

    if (!body.trim()) {
      setSeverity("error");
      setErrMsg("Enter the template body.");
      setOpen(true);
      return;
    }

    /*
     * Validate variable examples on the client before
     * sending the template to the backend.
     */
    const componentList = buildComponents();

    for (const component of componentList) {
      if (component.type === "HEADER" && component.format === "TEXT") {
        const variables = component.variables || [];

        for (const variable of variables) {
          if (!variable.example.trim()) {
            setSeverity("error");
            setErrMsg(`Enter an example value for ${variable.code}.`);
            setOpen(true);
            return;
          }
        }
      }

      if (component.type === "BODY") {
        const variables = component.variables || [];

        for (const variable of variables) {
          if (!variable.example.trim()) {
            setSeverity("error");
            setErrMsg(`Enter an example value for ${variable.code}.`);
            setOpen(true);
            return;
          }
        }
      }

      if (component.type === "BUTTONS") {
        for (const button of component.buttons || []) {
          const variables = button.variables || [];

          for (const variable of variables) {
            if (!variable.example.trim()) {
              setSeverity("error");
              setErrMsg(`Enter an example value for ${variable.code}.`);
              setOpen(true);
              return;
            }
          }
        }
      }
    }

    const json = {
      reference: ref,
      title: title.trim(),
      template_code: templateCode.trim().toLowerCase(),
      category: category,
      language: language,
      components: componentList,
    };
    console.log(json);

    setShowLoading(true);

    await axios
      .post(`/portal/wacc/businesses/templates/create.json`, json)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message || "Template submitted for approval.");
          setOpen(true);

          navigate(`/wacc/businesses/${ref}/templates`);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Action Failed");
        }
      })
      .catch(async (error) => {
        setShowLoading(false);
        await statusDialog.show("Unable to complete request.", "error", "Request Failed");
      });
  };

  /*
   * -----------------------------------------
   * DERIVED VARIABLES
   * -----------------------------------------
   */

  const bodyVariables = extractVariables(body);

  const headerVariables =
    headerEnabled && headerFormat === "TEXT" ? extractVariables(headerText) : [];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <MDBox mt={1} mb={5}>
        <MDBox mb={2}>
          <Button
            color="dark"
            size="small"
            startIcon={<Icon>west</Icon>}
            onClick={() => {
              navigate(`/wacc/businesses/${ref}/templates`);
            }}
          >
            Back
          </Button>
        </MDBox>

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Create Template
                </MDTypography>

                <MDBox mt={3}>
                  <MDInput
                    fullWidth
                    label="Template Name"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </MDBox>

                <MDBox mt={3}>
                  <MDInput
                    fullWidth
                    label="Template Code"
                    value={templateCode}
                    onChange={(e) => setTemplateCode(e.target.value)}
                    helperText="Use lowercase letters, numbers and underscores."
                  />
                </MDBox>

                <MDBox mt={3.5}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>

                    <Select
                      value={category}
                      label="Category"
                      onChange={(e) => setCategory(e.target.value)}
                      sx={{
                        padding: "12px",
                        paddingLeft: "3px",
                      }}
                    >
                      <MenuItem value="UTILITY">Utility</MenuItem>

                      <MenuItem value="MARKETING">Marketing</MenuItem>

                      <MenuItem value="AUTHENTICATION">Authentication</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>

                <Divider sx={{ my: 3 }} />

                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="h6">Header</MDTypography>

                  <Switch
                    checked={headerEnabled}
                    onChange={(e) => {
                      setHeaderEnabled(e.target.checked);

                      if (!e.target.checked) {
                        setHeaderText("");
                      }
                    }}
                  />
                </MDBox>

                {headerEnabled && (
                  <MDBox mt={2}>
                    <FormControl fullWidth>
                      <InputLabel>Header Type</InputLabel>

                      <Select
                        value={headerFormat}
                        label="Header Type"
                        onChange={(e) => setHeaderFormat(e.target.value)}
                        sx={{
                          padding: "12px",
                          paddingLeft: "3px",
                        }}
                      >
                        <MenuItem value="TEXT">Text</MenuItem>

                        <MenuItem value="IMAGE">Image</MenuItem>

                        <MenuItem value="VIDEO">Video</MenuItem>

                        <MenuItem value="DOCUMENT">Document</MenuItem>

                        <MenuItem value="LOCATION">Location</MenuItem>
                      </Select>
                    </FormControl>
                  </MDBox>
                )}

                {headerEnabled && headerFormat === "TEXT" && (
                  <MDBox mt={3}>
                    <MDInput
                      fullWidth
                      multiline
                      rows={2}
                      label="Header Text"
                      value={headerText}
                      onChange={(e) => setHeaderText(e.target.value)}
                    />

                    <MDTypography mt={1} variant="caption" color="text">
                      Insert variables using {"{{variable_name}}"}.
                    </MDTypography>

                    {renderVariableEditor(headerVariables)}
                  </MDBox>
                )}

                <Divider sx={{ my: 3 }} />

                <MDTypography variant="h6">Body</MDTypography>

                <MDBox mt={2}>
                  <MDInput
                    fullWidth
                    multiline
                    rows={6}
                    label="Message Body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                  />
                </MDBox>

                <MDTypography mt={1} variant="caption" color="text">
                  Insert variables using {"{{variable_name}}"}.
                </MDTypography>

                {renderVariableEditor(bodyVariables)}

                <Divider sx={{ my: 3 }} />

                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="h6">Footer</MDTypography>

                  <Switch
                    checked={footerEnabled}
                    onChange={(e) => {
                      setFooterEnabled(e.target.checked);

                      if (!e.target.checked) {
                        setFooter("");
                      }
                    }}
                  />
                </MDBox>

                {footerEnabled && (
                  <MDBox mt={2}>
                    <MDInput
                      fullWidth
                      label="Footer"
                      value={footer}
                      onChange={(e) => setFooter(e.target.value)}
                    />
                  </MDBox>
                )}

                <Divider sx={{ my: 3 }} />

                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <MDTypography variant="h6">Buttons</MDTypography>

                  <MDButton
                    variant="outlined"
                    color="dark"
                    onClick={addButton}
                    size="small"
                    iconOnly
                  >
                    <Icon>add</Icon>
                  </MDButton>
                </MDBox>

                {buttons.map((button, index) => {
                  const urlVariables =
                    button.type === "URL" ? extractVariables(button.url || "") : [];

                  return (
                    <MDBox
                      key={index}
                      mb={2}
                      p={2}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>Type</InputLabel>

                            <Select
                              value={button.type}
                              label="Type"
                              onChange={(e) => updateButton(index, "type", e.target.value)}
                              sx={{
                                padding: "12px",
                                paddingLeft: "3px",
                              }}
                            >
                              <MenuItem value="QUICK_REPLY">Quick Reply</MenuItem>

                              <MenuItem value="URL">URL</MenuItem>

                              <MenuItem value="PHONE_NUMBER">Phone Number</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={7}>
                          <MDInput
                            fullWidth
                            label="Button Text"
                            value={button.text || ""}
                            onChange={(e) => updateButton(index, "text", e.target.value)}
                          />
                        </Grid>

                        <Grid item xs={12} md={1}>
                          <MDButton color="error" iconOnly onClick={() => removeButton(index)}>
                            <Icon>delete</Icon>
                          </MDButton>
                        </Grid>

                        {button.type === "URL" && (
                          <Grid item xs={12}>
                            <MDInput
                              fullWidth
                              label="URL"
                              value={button.url || ""}
                              onChange={(e) => updateButton(index, "url", e.target.value)}
                            />

                            <MDTypography mt={1} variant="caption" color="text">
                              Insert variables using {"{{variable_name}}"}.
                            </MDTypography>

                            {urlVariables.length > 0 && (
                              <MDBox mt={2}>
                                <MDTypography
                                  variant="button"
                                  fontWeight="medium"
                                  display="block"
                                  mb={0.5}
                                >
                                  Personalization
                                </MDTypography>

                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  mb={1.5}
                                >
                                  Provide an example value for each URL variable.
                                </MDTypography>

                                {urlVariables.map((code) => (
                                  <MDBox
                                    key={code}
                                    mb={1.5}
                                    p={1.5}
                                    sx={{
                                      border: "1px solid",
                                      borderColor: "divider",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="medium"
                                      display="block"
                                      mb={0.75}
                                    >
                                      {formatVariableLabel(code)}
                                    </MDTypography>

                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      mb={1}
                                    >
                                      {"{{" + code + "}}"}
                                    </MDTypography>

                                    <MDInput
                                      fullWidth
                                      size="small"
                                      placeholder={`Example for {{${code}}}`}
                                      value={variableExamples[code] || ""}
                                      onChange={(e) => updateVariableExample(code, e.target.value)}
                                    />
                                  </MDBox>
                                ))}
                              </MDBox>
                            )}
                          </Grid>
                        )}

                        {button.type === "PHONE_NUMBER" && (
                          <Grid item xs={12}>
                            <MDInput
                              fullWidth
                              label="Phone Number"
                              value={button.phone_number || ""}
                              onChange={(e) => updateButton(index, "phone_number", e.target.value)}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </MDBox>
                  );
                })}
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card
              sx={{
                position: "sticky",
                top: 20,
                overflow: "hidden",
              }}
            >
              <MDBox p={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Template Preview
                </MDTypography>
              </MDBox>

              <MDBox
                sx={{
                  backgroundColor: "#efe7dd",
                  backgroundImage:
                    "radial-gradient(circle at 20% 20%, rgba(120,100,80,0.06) 1px, transparent 1px), radial-gradient(circle at 80% 70%, rgba(120,100,80,0.05) 1px, transparent 1px)",
                  backgroundSize: "24px 24px",
                  p: 2,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <MDBox
                  sx={{
                    width: "100%",
                    maxWidth: 490,
                    backgroundColor: "#fff",
                    borderRadius: "9px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                    overflow: "hidden",
                  }}
                >
                  <MDBox px={2} py={1.75}>
                    {headerEnabled && headerFormat === "TEXT" && headerText && (
                      <MDTypography
                        variant="h5"
                        fontWeight="bold"
                        sx={{
                          fontSize: "1.15rem",
                          lineHeight: 1.25,
                          mb: 1,
                        }}
                      >
                        {renderPreviewText(headerText)}
                      </MDTypography>
                    )}

                    {headerEnabled && headerFormat === "IMAGE" && (
                      <MDBox
                        sx={{
                          height: 80,
                          mb: 1.5,
                          borderRadius: "7px",
                          backgroundColor: "#f1f1f1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: 30,
                            color: "text.secondary",
                          }}
                        >
                          image
                        </Icon>
                      </MDBox>
                    )}

                    {headerEnabled && headerFormat === "VIDEO" && (
                      <MDBox
                        sx={{
                          height: 80,
                          mb: 1.5,
                          borderRadius: "7px",
                          backgroundColor: "#f1f1f1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: 30,
                            color: "text.secondary",
                          }}
                        >
                          videocam
                        </Icon>
                      </MDBox>
                    )}

                    {headerEnabled && headerFormat === "DOCUMENT" && (
                      <MDBox
                        sx={{
                          height: 80,
                          mb: 1.5,
                          borderRadius: "7px",
                          backgroundColor: "#f1f1f1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: 30,
                            color: "text.secondary",
                          }}
                        >
                          description
                        </Icon>
                      </MDBox>
                    )}

                    {headerEnabled && headerFormat === "LOCATION" && (
                      <MDBox
                        sx={{
                          height: 80,
                          mb: 1.5,
                          borderRadius: "7px",
                          backgroundColor: "#f1f1f1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: 30,
                            color: "text.secondary",
                          }}
                        >
                          location_on
                        </Icon>
                      </MDBox>
                    )}

                    <MDTypography
                      variant="body2"
                      sx={{
                        fontSize: "0.95rem",
                        lineHeight: 1.5,
                        color: "#303030",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {renderPreviewText(body)}
                    </MDTypography>

                    {footerEnabled && footer && (
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          mt: 1,
                          fontSize: "0.7rem",
                        }}
                      >
                        {footer}
                      </MDTypography>
                    )}
                  </MDBox>

                  {buttons.length > 0 && (
                    <MDBox
                      sx={{
                        borderTop: "1px solid #e0e0e0",
                      }}
                    >
                      {buttons.map((button, index) => (
                        <MDBox
                          key={index}
                          sx={{
                            py: 0.8,
                            textAlign: "center",
                            borderBottom: index < buttons.length - 1 ? "1px solid #e0e0e0" : "none",
                          }}
                        >
                          <MDTypography
                            variant="button"
                            sx={{
                              color: "#168c7b",
                              fontSize: "0.85rem",
                              fontWeight: 500,
                            }}
                          >
                            {button.text || "Button"}
                          </MDTypography>
                        </MDBox>
                      ))}
                    </MDBox>
                  )}

                  <MDBox px={1.5} pb={0.75} display="flex" justifyContent="flex-end">
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: "0.65rem",
                      }}
                    >
                      7:29 PM
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>

              <MDBox p={2} display="flex" justifyContent="flex-end" gap={1}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={() => {
                    navigate(`/wacc/businesses/${ref}/templates`);
                  }}
                >
                  Cancel
                </MDButton>

                <MDButton variant="contained" color="dark" onClick={saveTemplate}>
                  Save Template
                </MDButton>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default WhatsAppTemplateBuilderPage;
